import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import MenuBook from '@mui/icons-material/MenuBook';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';

function HftMemoir() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const sections = [
    {
      title: t('hft_page.sections.introduction.title'),
      content: t('hft_page.sections.introduction.content')
    },
    {
      title: t('hft_page.sections.infrastructure.title'),
      content: t('hft_page.sections.infrastructure.content'),
      items: t('hft_page.sections.infrastructure.items', { returnObjects: true })
    },
    {
      title: t('hft_page.sections.hardware.title'),
      content: t('hft_page.sections.hardware.content'),
      items: t('hft_page.sections.hardware.items', { returnObjects: true })
    },
    {
      title: t('hft_page.sections.algorithms.title'),
      content: t('hft_page.sections.algorithms.content'),
      items: t('hft_page.sections.algorithms.items', { returnObjects: true })
    },
    {
      title: t('hft_page.sections.data.title'),
      content: t('hft_page.sections.data.content')
    },
    {
      title: t('hft_page.sections.risks.title'),
      content: t('hft_page.sections.risks.content')
    },
  ];

  return (
    <>
      <SEO 
        title={`Mémoire HFT - ${t('hero.name')}`}
        description={t('hft_page.header.description')}
        type="article"
      />
      <Box sx={{ pb: 10, position: 'relative', overflowX: 'hidden', width: '100%' }}>
        {/* Header Background */}
        <Box
        sx={{
          pt: 12,
          pb: 8,
          width: '100%',
          backgroundColor: '#0e1626',
          backgroundImage: 'linear-gradient(135deg, rgba(78, 205, 196, 0.15) 0%, rgba(14, 22, 38, 0.9) 100%)',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #1f2a3d',
          overflowX: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<ArrowBack />}
              sx={{ color: '#fff' }}
            >
              {t('hft_page.back')}
            </Button>
          </Stack>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              mb: 2,
              color: '#fff',
            }}
          >
            {t('hft_page.title')}
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: '#a3a3a3',
              mb: 3,
              fontWeight: 400,
            }}
          >
            {t('hft_page.subtitle')}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip 
              label="Finance" 
              sx={{ 
                bgcolor: 'rgba(78, 205, 196, 0.2)', 
                color: '#4ecdc4', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Trading Systems" 
              sx={{ 
                bgcolor: 'rgba(255, 215, 61, 0.2)', 
                color: '#ffd93d', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Research" 
              sx={{ 
                bgcolor: 'rgba(255, 107, 107, 0.2)', 
                color: '#ff6b6b', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Master MIAGE" 
              sx={{ 
                bgcolor: 'rgba(78, 205, 196, 0.2)', 
                color: '#4ecdc4', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        {sections.map((section, idx) => (
          <Box key={idx} sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#fff',
              }}
            >
              {section.title}
            </Typography>
            
            {section.content && (
              <Typography
                variant="body1"
                sx={{
                  color: '#d4d4d4',
                  lineHeight: 1.8,
                  mb: section.items ? 3 : 0,
                }}
              >
                {section.content}
              </Typography>
            )}

            {section.items && (
              <Box component="ul" sx={{ pl: 3, color: '#d4d4d4' }}>
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} style={{ marginBottom: '12px', lineHeight: 1.8 }}>
                    <Typography variant="body1" component="span">
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Box>
        ))}

        {/* Key Themes */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('hft_page.themes.title')}
          </Typography>

          <Grid container spacing={3}>
            {t('hft_page.themes.items', { returnObjects: true }).map((theme, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: '#141414',
                    border: '2px solid #2a2a2a',
                    borderRadius: '12px',
                    height: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                      boxShadow: `0 8px 24px ${idx % 3 === 0 ? 'rgba(255, 107, 107, 0.2)' : idx % 3 === 1 ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 215, 61, 0.2)'}`,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                    }}
                  >
                    {theme.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#a3a3a3',
                      lineHeight: 1.7,
                    }}
                  >
                    {theme.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Conclusion */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: '#141414',
            border: '2px solid #2a2a2a',
            borderRadius: '12px',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '6px',
              background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #ffd93d)',
              zIndex: 1,
            },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <MenuBook sx={{ fontSize: 32, color: '#4ecdc4' }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {t('hft_page.conclusion.title')}
            </Typography>
          </Stack>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
            }}
          >
            {t('hft_page.conclusion.content')}
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
}

export default HftMemoir;

